'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { parseApiAsset, ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { LargeAssetInput } from '@morpho-org/ui-v6/client';
import { useState } from 'react';
import { useActionFlowReview } from '@/components/ActionFlow/useActionFlowReview';
import { ActionButtons } from '@/components/common/ActionButtons';
import BundleAuthorizationCallout from '@/components/common/BundleAuthorizationCallout/BundleAuthorizationCallout';
import { BUNDLE_NOT_AUTHORIZED_MESSAGE, EXIT_BUNDLE_NOT_ENABLED_MESSAGE } from '@/components/common/BundleAuthorizationCallout/messages';
import { TextError } from '@/components/common/Error';
import { WrapSegment } from '@/components/common/WrapSegment';
import { vaultV2DepositReviewDialogs } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/vaultV2DepositReviewDialogs';
import { getParsedValue } from '@/helpers/formatters';
import { isVaultV2Gated } from '@/helpers/vault';
import useBundleCanTransferOnchain from '@/hooks/operation/useBundleCanTransferOnchain';
import type { VaultV2ActionPanelData } from '../vaultV2ActionPanel.mapper';
import { useVaultV2DepositLogic } from '../../hooks/useVaultV2DepositLogic';
import InfoCallout from './InfoCallout';
import { VaultV2Summary } from './VaultV2Summary';
interface VaultV2OperationDepositProps {
  vaultData: VaultV2ActionPanelData;
  vaultAddress: Address;
}
const VaultV2OperationDeposit = t0 => {
  const $ = _c(80);
  const {
    vaultAddress,
    vaultData
  } = t0;
  const [isInputPending, setIsInputPending] = useState(false);
  const {
    open,
    element,
    isOpen: isReviewing
  } = useActionFlowReview("vaultV2Deposit", vaultV2DepositReviewDialogs);
  const t1 = vaultData?.asset;
  let t2;
  if ($[0] !== t1) {
    t2 = getParsedValue(t1, parseApiAsset);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const vaultAsset = t2;
  const {
    canTransfer: bundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultBundlesV1", vaultAsset?.address, vaultData?.asset?.chain?.id);
  const {
    canTransfer: exitBundleCanTransfer
  } = useBundleCanTransferOnchain("bundles.vaultExitBundlesV1", vaultAsset?.address, vaultData?.asset?.chain?.id);
  let t3;
  if ($[2] !== isReviewing || $[3] !== vaultAddress || $[4] !== vaultData) {
    t3 = {
      vaultData,
      vaultAddress,
      isReviewing
    };
    $[2] = isReviewing;
    $[3] = vaultAddress;
    $[4] = vaultData;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    inputAmount,
    setInputAmount,
    isMax,
    onMaxClick,
    maxDeposit,
    userVaultAssets,
    finalPosition,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    error,
    disabledLabel,
    isDepositDisabled,
    refetchPosition,
    refetchVaultData,
    resetInput,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    erc20Amount,
    nativeAmount,
    wrappableNativeBalance
  } = useVaultV2DepositLogic(t3);
  let t4;
  if ($[6] !== erc20Amount || $[7] !== finalPosition || $[8] !== inputAmount || $[9] !== nativeAmount || $[10] !== open || $[11] !== refetchPosition || $[12] !== refetchVaultData || $[13] !== resetInput || $[14] !== userVaultAssets || $[15] !== vaultAddress || $[16] !== vaultAsset) {
    t4 = () => {
      if (!isDefined(userVaultAssets) || !vaultAsset) {
        throw new Error("Missing data to open review dialog");
      }
      open({
        vaultAddress,
        inputAmount,
        erc20Amount,
        nativeAmount,
        vaultAsset,
        userPosition: userVaultAssets,
        finalPosition,
        onTxSuccess: resetInput,
        refetchPosition,
        refetchVaultData,
        entryPoint: "vault_page"
      });
    };
    $[6] = erc20Amount;
    $[7] = finalPosition;
    $[8] = inputAmount;
    $[9] = nativeAmount;
    $[10] = open;
    $[11] = refetchPosition;
    $[12] = refetchVaultData;
    $[13] = resetInput;
    $[14] = userVaultAssets;
    $[15] = vaultAddress;
    $[16] = vaultAsset;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const openReviewDialog = t4;
  const t5 = `Deposit ${vaultAsset?.symbol}`;
  const t6 = maxDeposit?.value;
  const t7 = vaultData?.gates;
  let t8;
  if ($[18] !== t7) {
    t8 = isVaultV2Gated(t7);
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== inputAmount || $[21] !== isMax || $[22] !== isMaxLoading || $[23] !== onMaxClick || $[24] !== setInputAmount || $[25] !== t5 || $[26] !== t6 || $[27] !== t8 || $[28] !== vaultAsset) {
    t9 = <LargeAssetInput header={t5} token={vaultAsset} amount={inputAmount} onChange={setInputAmount} onPendingChange={setIsInputPending} maxValue={t6} isMaxLoading={isMaxLoading} isMax={isMax} onMaxClick={onMaxClick} disabled={t8} autoFocus={true} />;
    $[20] = inputAmount;
    $[21] = isMax;
    $[22] = isMaxLoading;
    $[23] = onMaxClick;
    $[24] = setInputAmount;
    $[25] = t5;
    $[26] = t6;
    $[27] = t8;
    $[28] = vaultAsset;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== error.userBalanceError) {
    t10 = error.userBalanceError && <div className="flex justify-end"><TextError message="Error fetching wallet balance." messageTooltip={error.userBalanceError.message} /></div>;
    $[30] = error.userBalanceError;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== error.nativeBalanceError) {
    t11 = error.nativeBalanceError && <div className="flex justify-end"><TextError message="Error fetching native balance." messageTooltip={error.nativeBalanceError.message} /></div>;
    $[32] = error.nativeBalanceError;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== error.userVaultPositionError) {
    t12 = error.userVaultPositionError && <div className="flex justify-end"><TextError message="Error fetching vault position." messageTooltip={error.userVaultPositionError.message} /></div>;
    $[34] = error.userVaultPositionError;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12 || $[39] !== t9) {
    t13 = <div className="gap-2xs flex flex-col">{t9}{t10}{t11}{t12}</div>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t9;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const t14 = vaultAsset?.symbol;
  let t15;
  if ($[41] !== isWNativeVault || $[42] !== isWrapping || $[43] !== isWrappingToggleLoading || $[44] !== nativeSymbol || $[45] !== onWrappingChange || $[46] !== t14 || $[47] !== wrappableNativeBalance) {
    t15 = <WrapSegment id="vault-v2-deposit" enabled={true} isWNative={isWNativeVault} wrappableNativeBalance={wrappableNativeBalance} wrappedSymbol={t14} nativeSymbol={nativeSymbol} isWrapping={isWrapping} onWrappingChange={onWrappingChange} disabled={isWrappingToggleLoading} />;
    $[41] = isWNativeVault;
    $[42] = isWrapping;
    $[43] = isWrappingToggleLoading;
    $[44] = nativeSymbol;
    $[45] = onWrappingChange;
    $[46] = t14;
    $[47] = wrappableNativeBalance;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== vaultData.maxRate) {
    t16 = vaultData.maxRate === ZERO && <InfoCallout variant="warning" data-testid="vault-v2-interest-paused-deposit">Interest paused. Deposits earn no interest until the Curator raises it.</InfoCallout>;
    $[49] = vaultData.maxRate;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== finalPosition || $[52] !== isLoading || $[53] !== userVaultAssets || $[54] !== vaultAddress || $[55] !== vaultAsset || $[56] !== vaultData) {
    t17 = <VaultV2Summary vaultAsset={vaultAsset} vaultAddress={vaultAddress} vaultData={vaultData} initialPosition={userVaultAssets} finalPosition={finalPosition} isLoading={isLoading} />;
    $[51] = finalPosition;
    $[52] = isLoading;
    $[53] = userVaultAssets;
    $[54] = vaultAddress;
    $[55] = vaultAsset;
    $[56] = vaultData;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== bundleCanTransfer) {
    t18 = !bundleCanTransfer && <BundleAuthorizationCallout variant="destructive" message={BUNDLE_NOT_AUTHORIZED_MESSAGE} />;
    $[58] = bundleCanTransfer;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== exitBundleCanTransfer) {
    t19 = !exitBundleCanTransfer && <BundleAuthorizationCallout variant="warning" message={EXIT_BUNDLE_NOT_ENABLED_MESSAGE} />;
    $[60] = exitBundleCanTransfer;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  const t20 = isLoading && !isDepositDisabled;
  const t21 = disabledLabel ?? "Review Deposit";
  let t22;
  if ($[62] !== tooltipMessage) {
    t22 = tooltipMessage ? <span className="text-3xs" style={tooltipMessage.textStyle}>{tooltipMessage.message}</span> : undefined;
    $[62] = tooltipMessage;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const t23 = !!disabledLabel || !bundleCanTransfer;
  let t24;
  if ($[64] !== isInputPending || $[65] !== openReviewDialog || $[66] !== t20 || $[67] !== t21 || $[68] !== t22 || $[69] !== t23) {
    t24 = <ActionButtons handleFinalize={openReviewDialog} isLoading={t20} finalizeLabel={t21} tooltipContent={t22} isFinalizeDisabled={t23} isInputPending={isInputPending} />;
    $[64] = isInputPending;
    $[65] = openReviewDialog;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== element || $[72] !== t13 || $[73] !== t15 || $[74] !== t16 || $[75] !== t17 || $[76] !== t18 || $[77] !== t19 || $[78] !== t24) {
    t25 = <>{element}{t13}{t15}{t16}{t17}{t18}{t19}{t24}</>;
    $[71] = element;
    $[72] = t13;
    $[73] = t15;
    $[74] = t16;
    $[75] = t17;
    $[76] = t18;
    $[77] = t19;
    $[78] = t24;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  return t25;
};
export default VaultV2OperationDeposit;